<template>
    <div class="kpi-grid">
        <router-link
            v-for="kpi in kpis"
            :key="kpi.id"
            :to="kpi.to"
            class="kpi-card"
        >
            <span class="kpi-label">{{ kpi.label }}</span>

            <div class="kpi-value-row">
                <span class="kpi-value">{{ kpi.value }}</span>
                <span v-if="kpi.delta !== null" class="kpi-delta" :class="deltaClass(kpi)">
                    <i class="fal" :class="kpi.delta >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
                    {{ kpi.delta >= 0 ? '+' : '' }}{{ kpi.delta }}
                </span>
            </div>

            <svg class="kpi-spark" viewBox="0 0 100 32" preserveAspectRatio="none" aria-hidden="true">
                <defs>
                    <linearGradient :id="`kpi-grad-${kpi.id}`" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" :stop-color="kpi.color" stop-opacity="0.22" />
                        <stop offset="100%" :stop-color="kpi.color" stop-opacity="0" />
                    </linearGradient>
                </defs>
                <path :d="areaPath(kpi.sparkline)" :fill="`url(#kpi-grad-${kpi.id})`" />
                <path :d="linePath(kpi.sparkline)" fill="none" :stroke="kpi.color" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        </router-link>
    </div>
</template>

<script>
export default {
    name: 'HomeCardsGallery',
    props: [
        'myposts_new_this_week',
        'myposts_processing_this_week',
        'myposts_done_this_week',
        'new_this_week',
        'processing_this_week',
        'done_this_week',
        'myposts_previous_new',
        'myposts_previous_processing',
        'myposts_previous_done',
        'previous_new',
        'previous_processing',
        'previous_done',
        'period'
    ],
    computed: {
        sparkConfig() {
            switch (this.period) {
                case 'today':
                    return { points: 12, noise: 0.25, trend: 0.35 }
                case '7days':
                    return { points: 7, noise: 0.5, trend: 0.5 }
                case '30days':
                default:
                    return { points: 20, noise: 0.7, trend: 0.7 }
            }
        },
        kpis() {
            const myOpenCurrent = this.myposts_new_this_week + this.myposts_processing_this_week
            const myOpenPrevious = this.myposts_previous_new + this.myposts_previous_processing
            return [
                {
                    id: 'new',
                    label: this.$t('post.stats.new_this_week') || 'Nouveaux cette semaine',
                    value: this.new_this_week,
                    previous: this.previous_new,
                    delta: this.computeDelta(this.new_this_week, this.previous_new),
                    color: 'var(--v2-warning)',
                    sparkline: this.evolutionSparkline(this.previous_new, this.new_this_week, 1),
                    to: '/posts?status=newposts'
                },
                {
                    id: 'processing',
                    label: this.$t('post.stats.in_progress') || 'En traitement',
                    value: this.processing_this_week,
                    previous: this.previous_processing,
                    delta: this.computeDelta(this.processing_this_week, this.previous_processing),
                    color: 'var(--v2-primary)',
                    sparkline: this.evolutionSparkline(this.previous_processing, this.processing_this_week, 2),
                    to: '/posts?status=openedposts'
                },
                {
                    id: 'done',
                    label: this.$t('post.stats.done_this_week') || 'Terminés',
                    value: this.done_this_week,
                    previous: this.previous_done,
                    delta: this.computeDelta(this.done_this_week, this.previous_done),
                    color: 'var(--v2-success)',
                    sparkline: this.evolutionSparkline(this.previous_done, this.done_this_week, 3),
                    to: '/posts?status=doneposts'
                },
                {
                    id: 'mine',
                    label: this.$t('post.stats.my_open') || 'Mes tickets ouverts',
                    value: myOpenCurrent,
                    previous: myOpenPrevious,
                    delta: this.computeDelta(myOpenCurrent, myOpenPrevious),
                    color: '#8b5cf6',
                    sparkline: this.evolutionSparkline(myOpenPrevious, myOpenCurrent, 4),
                    to: '/posts?status=myopenedposts'
                }
            ]
        }
    },
    methods: {
        evolutionSparkline(previous, current, seed) {
            const { points, noise } = this.sparkConfig
            const start = Number(previous) || 0
            const end = Number(current) || 0
            if (start === end) {
                return Array.from({ length: points }, () => end)
            }
            const amplitude = Math.max(Math.abs(end - start), Math.max(start, end, 1)) * noise * 0.35
            return Array.from({ length: points }, (_, i) => {
                const progress = i / (points - 1)
                const base = start + (end - start) * progress
                const wave = Math.sin(seed * 1.7 + i * 1.1)
                const jitter = wave * amplitude * (1 - Math.abs(progress - 0.5) * 0.6)
                return Math.max(0, base + jitter)
            })
        },
        computeDelta(current, previous) {
            const cur = Number(current) || 0
            const prev = Number(previous) || 0
            if (cur === 0 && prev === 0) return null
            return cur - prev
        },
        deltaClass(kpi) {
            if (kpi.delta === 0) return 'kpi-delta--neutral'
            const goodDirection = kpi.id === 'done' ? 1 : -1
            const isGood = Math.sign(kpi.delta) === goodDirection
            return isGood ? 'kpi-delta--good' : 'kpi-delta--bad'
        },
        normalize(points) {
            const min = Math.min(...points)
            const max = Math.max(...points)
            if (max === min) return points.map(() => 0.5)
            const span = max - min
            return points.map(p => (p - min) / span)
        },
        linePath(points) {
            const norm = this.normalize(points)
            const n = norm.length
            return norm.map((p, i) => {
                const x = (i / (n - 1)) * 100
                const y = 28 - p * 24
                return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
            }).join(' ')
        },
        areaPath(points) {
            const norm = this.normalize(points)
            const n = norm.length
            const line = norm.map((p, i) => {
                const x = (i / (n - 1)) * 100
                const y = 28 - p * 24
                return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
            }).join(' ')
            return `${line} L 100 32 L 0 32 Z`
        }
    }
}
</script>

<style scoped>
.kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: var(--v2-space-4);
    width: 100%;
}

.kpi-card {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-3);
    padding: var(--v2-space-5);
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    text-decoration: none !important;
    color: inherit;
    transition: border-color var(--v2-transition-fast),
        box-shadow var(--v2-transition-fast),
        transform var(--v2-transition-fast);
    min-height: 150px;
    justify-content: space-between;
}

.kpi-card:hover {
    border-color: var(--v2-primary);
    box-shadow: var(--v2-shadow-sm);
    transform: translateY(-1px);
}

.kpi-label {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    font-weight: var(--v2-font-weight-medium);
    letter-spacing: 0.01em;
}

.kpi-value-row {
    display: flex;
    align-items: baseline;
    gap: var(--v2-space-2);
}

.kpi-value {
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    line-height: 1;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
}

.kpi-delta {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    font-variant-numeric: tabular-nums;
}

.kpi-delta i {
    font-size: 0.7rem;
}

.kpi-delta--good {
    color: var(--v2-success);
    background: rgba(16, 185, 129, 0.12);
    padding: 2px 6px;
    border-radius: var(--v2-radius-sm);
}

.kpi-delta--bad {
    color: var(--v2-danger);
    background: rgba(239, 68, 68, 0.1);
    padding: 2px 6px;
    border-radius: var(--v2-radius-sm);
}

.kpi-delta--neutral {
    color: var(--v2-text-4);
    background: var(--v2-surface-2);
    padding: 2px 6px;
    border-radius: var(--v2-radius-sm);
}

.kpi-spark {
    width: 100%;
    height: 40px;
    display: block;
}

@media (max-width: 900px) {
    .kpi-grid {
        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
        gap: var(--v2-space-3);
    }

    .kpi-card {
        padding: var(--v2-space-4);
        min-height: 130px;
    }

    .kpi-value {
        font-size: 1.5rem;
    }
}
</style>
